import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import moment from 'moment';
import { saveAs } from 'file-saver';

interface DeviantReportState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;

    filter: {
        dZoneGroups: any;
        targetDates: Date[];
    };

    documents: any[];
}

const state: DeviantReportState = {
    loading: false,
    loaded: false,
    fatalError: false,

    filter: {
        dZoneGroups: null,
        targetDates: [new Date(), new Date()],
    },

    documents: [],
};

function buildFilter(params: any) {
    const queryParams: any = {}

    if (params.targetDates) {
        queryParams.date_from = moment(params.targetDates[0]).format('YYYY-MM-DD');
        queryParams.date_to = moment(params.targetDates[1]).add(1, 'day').format('YYYY-MM-DD')
    }

    if (params.dZoneGroups) {
        queryParams.delivery_zone_id = params.dZoneGroups;
    }
    if (params.employeeId) {
        queryParams.employee_id = params.employeeId;
    }

    return queryParams;
}

export const REPORT_DATA = 'REPORT_DATA';

export const actions: ActionTree<DeviantReportState, RootState> = {

    loadData({ commit, state, dispatch }, params) {
        const vm = (this as any)._vm;

        return new Promise((resolve, reject) => {

            if (Object.values(params).length > 0) {
                commit('START_LOADING');

                vm.$db([
                    vm.$q(REPORT_DATA)
                        .raw('documents.list')
                        .action({
                            columns: {
                                id: 's.id',
                                shift_date: 's.shift_date',
                                shift_id: 's.id',
                                number: 'spv.number',
                                base_name: 'vb.name',
                                extract_time: 'sfv.extract_time',
                                volume_name: 'vv.name',
                                volume_volume: 'vv.volume',
                                weight_netto: 'sfv.weight_neto',
                                lossless_loading: 'vb.lossless_loading',
                                lossy_loading: 'vb.lossy_loading',
                                loss_rate: 'vb.loss_rate',
                                overspending_rate: 'vb.overspending_rate',
                                surplus_rate: 'vb.surplus_rate',
                                filling_id: 'vf.id',
                                volume_type: 'vv.type',
                                volume_quantity: 'sfv.quantity',
                            },
                            from: "s:shifts",
                            from_as: "s", 
                            joins: [
                                {
                                    join: 's:shift_plan_vdp',
                                    type: 'left',
                                    as:   'spv',
                                    on: {
                                      $eq: ['spv.shift_id', 's.id']
                                    }
                                },
                                {
                                    join: 's:shift_fact_vdp',
                                    type: 'left',
                                    as:   'sfv',
                                    on: {
                                      $eq: ['sfv.shift_plan_id', 'spv.id']
                                    }
                                },
                                {
                                    join: 's:shift_journal_vdp',
                                    type: 'left',
                                    as:   'sjv',
                                    on: {
                                      $eq: ['sjv.fact_id', 'sfv.id']
                                    }
                                },
                                {
                                    join: 's:voc_bases',
                                    type: 'left',
                                    as:   'vb',
                                    on: {
                                      $eq: ['spv.base_id', 'vb.id']
                                    }
                                },
                                {
                                    join: 's:volume_fillfing',
                                    type: 'left',
                                    as:   'vf',
                                    on: {
                                      $eq: ['spv.volume_filling_id', 'vf.id']
                                    }
                                },
                                {
                                    join: 's:voc_volumes',
                                    type: 'left',
                                    as:   'vv',
                                    on: {
                                      $eq: ['vf.volume_id', 'vv.id']
                                    }
                                },
                            ],
                            page_size: 10000,
                            where: {
                                $and: [
                                    {$gte: ['s.shift_date', {$: params.targetDates[0]}]},
                                    {$lte: ['s.shift_date', {$: params.targetDates[1]}]},
                                    {$not: [{$in: ['spv.number', {$: ['0Н', '0Д']}]}]},
                                ]
                            },
                            groups: [
                                's.id',
                                's.shift_date',
                                'spv.number',
                                'vb.name',
                                'sfv.extract_time',
                                'vv.name',
                                'vv.volume',
                                'sfv.weight_neto',
                                'vb.lossless_loading',
                                'vb.lossy_loading',
                                'vb.loss_rate',
                                'vb.overspending_rate',
                                'vb.surplus_rate',
                                'vv.type',
                                'sfv.quantity',
                                'vf.id'
                            ],
                            order: [
                              {$asc: ['s.shift_date']}
                            ]
                        })
                ]).then(((result: ServerResult) => {
                    const content = result.getOperation(REPORT_DATA).getContent() 
                    commit('SET_REPORT_DATA', content.documents);
                    commit('LOADED_SUCCESS');
                    resolve();
                }));
            } else {
                resolve();
            }
        })
    },

     exportData({state, getters}, {columns, columnsList}) {
        return new Promise((resolve, reject) => {
            const csvContent: any[] = [];
            const subheader: any[] = [
                '',
                '',
                '',
                '',
                '',
                '',
                Math.round(getters.total.lossless_loading * 100) / 100,
                Math.round(getters.total.lossy_loading * 100) / 100,
                Math.round(getters.total.weight_netto * 100) / 100,
                Math.round(getters.total.weight_lossless * 100) / 100,
                Math.round(getters.total.surplus_overspending * 100) / 100,
                Math.round(getters.total.weight_total * 100) / 100,
                Math.round(getters.total.precent * 100) / 100,
            ]
            const colors: any[] = [];
            const header = [
                'Дата производства',
                '№ партии',
                'База',
                'Емкость для сквашивания',
                'Объем емкости для сквашивания',
                'Количество штук',
                'Вес нетто включая потери (расчетный), кг',
                'Норма выхода, кг',
                'Фактический выход нетто, кг',
                'Нормы потерь, кг',
                'Излишки (+)/Перерасход (-)) смеси, кг',
                'Итого потерь (включая нормы потерь), кг',
                'Нормы потерь, %',
                // 'Количественные отклонения (Излишки/Перерасход) смеси (включая норму потерь), %',
            ]

            state.documents.map((item: any, idx: number) => {
            const row: any[] = [];
            row.push(item.extract_time ? moment(item.extract_time).format('DD.MM.YYYY'): '')
            row.push(moment(item.shift_date).format('DD.MM.YYYY') + ' ' + (item.number ? item.number : ""))
            row.push(item.base_name)
            row.push(item.volume_name)
            row.push(item.volume_volume)
            row.push(item.volume_quantity)
            row.push(item.lossless_loading)
            row.push(item.lossy_loading)
            row.push(item.weight_netto)
            row.push(item.weight_lossless)
            row.push((item.weight_surplus > 0) ? item.weight_surplus : item.weight_overspending * -1)
            row.push(item.weight_total)
            row.push(item.loss_rate)
            // row.push((item.weight_surplus > 0) ? item.surplus_percent : item.overspending_percent)
            //     })
            csvContent.push(row)
            })
            resolve({csvContent: [
                  header,
                  subheader,
                  ...csvContent
            ], colors})
        })

    }
};

export const mutations: MutationTree<DeviantReportState> = {
    START_LOADING: (state) => {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },
    LOADED_SUCCESS: (state) => {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },
    LOADED_ERROR: (state) => {
        state.loaded = true;
        state.fatalError = true;
    },

    SET_REPORT_DATA(state, data) {
        state.documents = Object.values(data.reduce((acc: any, item: any) => {
            if (!acc[item.filling_id]) {
                acc[item.filling_id] = {
                    base_name: item.base_name,
                    shift_date: item.shift_date,
                    volume_name: item.volume_name,
                    volume_volume: item.volume_volume,
                    loss_rate: item.loss_rate,
                    overspending_rate: item.overspending_rate,
                    surplus_rate: item.surplus_rate,
                    volume_quantity: 0,
                    lossless_loading: 0,
                    lossy_loading: 0,
                    weight_netto: 0,
                    weight_lossless: 0,
                    weight_surplus: 0,
                    weight_overspending: 0,
                    weight_total: 0,
                    surplus_percent: 0,
                    overspending_percent: 0,
                    
                    items: []
                }
            } else {
                console.log('catch!')
            }
            acc[item.filling_id].items.push(item)
            acc[item.filling_id].volume_quantity += parseFloat(item.volume_quantity) || 0
            acc[item.filling_id].lossless_loading += parseFloat(item.lossless_loading) || 0
            acc[item.filling_id].lossy_loading += parseFloat(item.lossy_loading) || 0
            acc[item.filling_id].weight_netto += parseFloat(item.weight_netto) || 0
            acc[item.filling_id].weight_lossless = acc[item.filling_id].lossless_loading - acc[item.filling_id].lossy_loading
            acc[item.filling_id].weight_surplus = acc[item.filling_id].weight_netto - acc[item.filling_id].lossless_loading
            acc[item.filling_id].weight_surplus = acc[item.filling_id].weight_surplus > 0 ? acc[item.filling_id].weight_surplus : 0
            acc[item.filling_id].weight_total = acc[item.filling_id].lossless_loading - acc[item.filling_id].weight_netto
            acc[item.filling_id].weight_overspending = acc[item.filling_id].weight_total - acc[item.filling_id].weight_lossless
            acc[item.filling_id].weight_overspending = acc[item.filling_id].weight_overspending > 0 ? acc[item.filling_id].weight_overspending : 0;
            acc[item.filling_id].weight_total = acc[item.filling_id].weight_total > 0 ? acc[item.filling_id].weight_total : 0;
            
            acc[item.filling_id].overspending_percent = (acc[item.filling_id].lossy_loading > 0) ? (acc[item.filling_id].weight_overspending / acc[item.filling_id].lossy_loading  * 100) : 0
            acc[item.filling_id].surplus_percent = (acc[item.filling_id].lossless_loading > 0) ? (acc[item.filling_id].weight_surplus / acc[item.filling_id].lossless_loading * 100) : 0

            acc[item.filling_id].overspending_class = acc[item.filling_id].overspending_percent < acc[item.filling_id].overspending_rate ? 'clot-ok' : 'clot-warn'
            acc[item.filling_id].surplus_class = (acc[item.filling_id].surplus_percent < acc[item.filling_id].surplus_rate) ? 'clot-ok' : 'clot-warn'

            return acc
        }, {}));
    },
};

const getters = {
  total: (state: DeviantReportState) => {
    return state.documents.reduce((acc: any, item: any) => {
      acc.lossless_loading += parseFloat(item.lossless_loading) || 0
      acc.lossy_loading += parseFloat(item.lossy_loading) || 0
      acc.weight_netto += parseFloat(item.weight_netto) || 0
      acc.weight_lossless += parseFloat(item.weight_lossless) || 0
      acc.weight_total += parseFloat(item.weight_total) || 0
      acc.surplus_overspending += parseFloat(item.weight_surplus) || 0
      acc.surplus_overspending += parseFloat(item.weight_overspending) || 0
      acc.precent = (acc.weight_netto - acc.lossless_loading) / acc.lossless_loading * 100
      acc.loss_rate = acc.weight_lossless / acc.lossless_loading * 100
      return acc
    }, {
      lossless_loading: 0,
      lossy_loading: 0,
      weight_netto: 0,
      weight_lossless: 0,
      weight_total: 0,
      surplus_overspending: 0,
      precent: 0,
    });
  },
};

export const STORE_KEY = '$_deviant_report';

export const store = {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
};
